import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create an entity adapter for normalized state management
const bookedOrdersAdapter = createEntityAdapter({
  selectId: (order) => order._id, // Ensure each order is uniquely identified
});

const initialState = bookedOrdersAdapter.getInitialState({
  loading: false,
  error: null,
});

// Async thunk for fetching orders
export const fetchBookedOrders = createAsyncThunk(
  "bookedOrder/fetch",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      // Call the API to fetch orders
      const response = await request({
        endpoint: "/admin/getOrders", // API endpoint
        method: "GET",
        params: { startDate, endDate },
      });

      console.log("Response from fetchBookedOrders:", response);

      // Validate API response structure
      if (!response?.success || !Array.isArray(response?.data?.orders)) {
        return rejectWithValue(
          response?.error?.message || "Failed to fetch orders."
        );
      }

      return response.data.orders; // Returning only orders
    } catch ({ error }) {
      console.error("Error fetching orders:", error);
      return rejectWithValue(
        error.message || "An unexpected error occurred while fetching orders."
      );
    }
  }
);

const bookedOrderSlice = createSlice({
  name: "bookedOrder",
  initialState,
  reducers: {
    clearBookedOrders: (state) => {
      bookedOrdersAdapter.removeAll(state);
      state.loading = false;
      state.error = null;
    },
    addBookedOrderIO: bookedOrdersAdapter.addOne,
    updateBookedOrderIO: bookedOrdersAdapter.upsertOne,
    addBookedOrdersIO: bookedOrdersAdapter.setAll,
    addBookedOrdersIO2: bookedOrdersAdapter.addMany,
    removeBookedOrderIO: bookedOrdersAdapter.removeOne,
    removeBookedOrdersIO: bookedOrdersAdapter.removeMany,
    bookOrderIo: (state, action) => {
      const successOrders = action.payload.details?.successes?.orders || [];
      const failureOrders = action.payload.details?.failures || [];
      if (successOrders.length > 0) {
        bookedOrdersAdapter.upsertMany(state, successOrders);
        console.log("success orders");
      }

      if (failureOrders.length > 0) {
        console.log("failure orders");
        bookedOrdersAdapter.upsertMany(state, failureOrders);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookedOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookedOrders.fulfilled, (state, action) => {
        state.loading = false;
        bookedOrdersAdapter.setAll(state, action.payload); // Store fetched orders
      })
      .addCase(fetchBookedOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload; // Store error message
      });
  },
});

// Selectors
export const {
  selectAll: selectAllBookedOrders,
  selectById: selectBookedOrderById,
  selectIds: selectBookedOrderIds,
} = bookedOrdersAdapter.getSelectors((state) => state.bookedOrder);

export const {
  clearBookedOrders,
  addBookedOrderIO,
  updateBookedOrderIO,
  addBookedOrdersIO,
  removeBookedOrderIO,
  removeBookedOrdersIO,
  addBookedOrdersIO2,
  bookOrderIo,
} = bookedOrderSlice.actions;

export default bookedOrderSlice.reducer;
